<template>
    <div class="calendar-wrap">

        <!-- =====================================================
             HEADER
        ====================================================== -->
        <div class="calendar-header">

            <div class="calendar-title">
                <span class="calendar-icon">▦</span>
                My Events
            </div>

            <div class="calendar-header-actions">

                <!-- VIEW DROPDOWN -->
                <div class="dropdown">

                    <button
                        class="btn btn-sm calendar-view-btn dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                    >
                        {{ viewLabel }}
                    </button>

                    <ul class="dropdown-menu dropdown-menu-end">

                        <li>
                            <button
                                class="dropdown-item"
                                :class="{ active: currentView === 'month' }"
                                @click="changeView('month')"
                            >
                                Month
                            </button>
                        </li>

                        <li>
                            <button
                                class="dropdown-item"
                                :class="{ active: currentView === 'agenda' }"
                                @click="changeView('agenda')"
                            >
                                Agenda
                            </button>
                        </li>

                        <li>
                            <button
                                class="dropdown-item"
                                :class="{ active: currentView === 'today' }"
                                @click="changeView('today')"
                            >
                                Today
                            </button>
                        </li>

                    </ul>

                </div>

                <button class="calendar-header-btn">
                    ⚙
                </button>

                <button class="calendar-header-btn">
                    −
                </button>

                <button class="calendar-header-btn">
                    ×
                </button>

            </div>
        </div>


        <!-- =====================================================
             MONTH VIEW
        ====================================================== -->
        <template v-if="currentView === 'month'">

            <div class="calendar-toolbar">

                <div class="calendar-period-title">
                    {{ monthYear }}
                </div>

                <div class="calendar-navigation">

                    <button
                        class="calendar-nav-btn"
                        @click="previousPeriod"
                    >
                        ‹
                    </button>

                    <button
                        class="calendar-nav-btn"
                        @click="nextPeriod"
                    >
                        ›
                    </button>

                </div>

            </div>


            <div class="calendar-grid">

                <!-- WEEK DAYS -->
                <div
                    v-for="day in weekDayNames"
                    :key="day"
                    class="weekday"
                >
                    {{ day }}
                </div>


                <!-- MONTH DAYS -->
                <div
                    v-for="day in monthDays"
                    :key="day.key"
                    class="day"
                    :class="{
                        'other-month': !day.currentMonth,
                        'today-cell': isToday(day.date),
                        'drag-over': dragOverKey === day.key
                    }"
                    @dragover.prevent="dragOverKey = day.key"
                    @drop="dropOnDay(day.date)"
                >

                    <span class="day-number">
                        {{ day.date.getDate() }}
                    </span>


                    <div class="event-list">

                        <div
                            v-for="event in getEventsForDate(day.date)"
                            :key="event.id"
                            class="event"
                            :class="[eventClass(event), { 'event-dragging': dragEventId === event.id }]"
                            :draggable="!event.locked"
                            @dragstart="startDrag(event, $event)"
                            @dragend="endDrag"
                            @click="eventClicked(event)"
                        >

                            <span
                                v-if="event.locked"
                                class="event-lock"
                            >
                                🔒
                            </span>

                            <span
                                v-if="event.repeating"
                                class="event-repeat"
                            >
                                ◷
                            </span>

                            <span
                                v-if="event.completed"
                                class="event-check"
                            >
                                ✓
                            </span>

                            <div
                                v-if="event.start || event.allDay"
                                class="event-time"
                            >
                                {{ formatEventTime(event) }}
                            </div>

                            <div class="event-title">
                                {{ event.title }}
                            </div>

                            <div
                                v-if="event.description"
                                class="event-description"
                            >
                                {{ event.description }}
                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </template>


        <!-- =====================================================
             AGENDA VIEW
        ====================================================== -->
        <template v-if="currentView === 'agenda'">

            <div class="calendar-toolbar">

                <div class="calendar-period-title">
                    {{ agendaTitle }}
                </div>

                <div class="calendar-navigation">

                    <button
                        class="calendar-nav-btn"
                        @click="previousPeriod"
                    >
                        ‹
                    </button>

                    <button
                        class="calendar-nav-btn"
                        @click="nextPeriod"
                    >
                        ›
                    </button>

                </div>

            </div>


            <div class="agenda-calendar">

                <!-- TOP DATE HEADER -->
                <div class="agenda-days-header">

                    <div class="agenda-time-header"></div>

                    <div
                        v-for="day in agendaDays"
                        :key="day.key"
                        class="agenda-day-header"
                        :class="{ 'agenda-today': isToday(day.date) }"
                    >

                        <div class="agenda-day-name">
                            {{ formatDayName(day.date) }}
                        </div>

                        <div class="agenda-day-date">
                            {{ formatShortDate(day.date) }}
                        </div>

                    </div>

                </div>


                <!-- ALL DAY ROW -->
                <div class="agenda-all-day-row">

                    <div class="agenda-time-label">
                        all-day
                    </div>

                    <div
                        v-for="day in agendaDays"
                        :key="day.key"
                        class="agenda-all-day-cell"
                        @dragover.prevent
                        @drop="dropOnDay(day.date)"
                    >

                        <div
                            v-for="event in getAllDayEvents(day.date)"
                            :key="event.id"
                            class="agenda-event all-day-event"
                            :class="eventClass(event)"
                            :draggable="!event.locked"
                            @dragstart="startDrag(event, $event)"
                            @dragend="endDrag"
                            @click="eventClicked(event)"
                        >
                            {{ event.title }}
                        </div>

                    </div>

                </div>


                <!-- HOURLY GRID -->
                <div class="agenda-body">

                    <!-- TIME COLUMN -->
                    <div class="agenda-time-column">

                        <div
                            v-for="hour in hours"
                            :key="hour"
                            class="agenda-hour-label"
                        >
                            {{ formatHour(hour) }}
                        </div>

                    </div>


                    <!-- DAY COLUMNS -->
                    <div
                        v-for="day in agendaDays"
                        :key="day.key"
                        class="agenda-day-column"
                        :class="{ 'agenda-current-day': isToday(day.date) }"
                        @dragover.prevent
                        @drop="dropOnAgendaColumn(day.date, $event)"
                    >

                        <div
                            v-for="hour in hours"
                            :key="hour"
                            class="agenda-hour"
                        ></div>


                        <!-- EVENTS -->
                        <div
                            v-for="event in getTimedEvents(day.date)"
                            :key="event.id"
                            class="agenda-event"
                            :class="[eventClass(event), { 'event-dragging': dragEventId === event.id }]"
                            :style="eventPosition(event)"
                            :draggable="!event.locked"
                            @dragstart="startDrag(event, $event)"
                            @dragend="endDrag"
                            @click="eventClicked(event)"
                        >

                            <div class="agenda-event-time">
                                {{ formatAgendaEventTime(event) }}
                            </div>

                            <div class="agenda-event-title">
                                {{ event.title }}
                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </template>


        <!-- =====================================================
             TODAY VIEW
        ====================================================== -->
        <template v-if="currentView === 'today'">

            <div class="calendar-toolbar">

                <div class="calendar-period-title">
                    {{ todayTitle }}
                </div>

                <div class="calendar-navigation">

                    <button
                        class="calendar-nav-btn"
                        @click="previousPeriod"
                    >
                        ‹
                    </button>

                    <button
                        class="calendar-nav-btn"
                        @click="nextPeriod"
                    >
                        ›
                    </button>

                </div>

            </div>


            <div class="today-calendar">

                <!-- DAY HEADER -->
                <div class="today-day-header">

                    <div class="today-time-header"></div>

                    <div class="today-day-name">
                        {{ formatDayName(selectedDate) }}
                    </div>

                </div>


                <!-- ALL DAY -->
                <div class="today-all-day">

                    <div class="today-time-label">
                        all-day
                    </div>

                    <div class="today-all-day-content">

                        <div
                            v-for="event in getAllDayEvents(selectedDate)"
                            :key="event.id"
                            class="agenda-event all-day-event"
                            :class="eventClass(event)"
                        >
                            {{ event.title }}
                        </div>

                    </div>

                </div>


                <!-- HOURLY -->
                <div class="today-body">

                    <!-- TIME -->
                    <div class="today-time-column">

                        <div
                            v-for="hour in hours"
                            :key="hour"
                            class="today-hour-label"
                        >
                            {{ formatHour(hour) }}
                        </div>

                    </div>


                    <!-- DAY -->
                    <div class="today-day-column">

                        <div
                            v-for="hour in hours"
                            :key="hour"
                            class="today-hour"
                        ></div>


                        <!-- EVENTS -->
                        <div
                            v-for="event in getTimedEvents(selectedDate)"
                            :key="event.id"
                            class="today-event"
                            :class="eventClass(event)"
                            :style="eventPosition(event)"
                            @click="eventClicked(event)"
                        >

                            <div class="agenda-event-time">
                                {{ formatAgendaEventTime(event) }}
                            </div>

                            <div class="agenda-event-title">
                                {{ event.title }}
                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </template>

    </div>
</template>


<script setup>

import { computed, ref } from 'vue'

// Tells the page when an event was moved (later: save it to the backend)
const emit = defineEmits(['event-moved'])


/* ============================================================
   CURRENT VIEW
============================================================ */

const currentView = ref('month')


/* ============================================================
   SELECTED DATE
============================================================ */

const selectedDate = ref(new Date())


/* ============================================================
   WEEK DAYS
============================================================ */

const weekDayNames = [
    'Sun',
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat'
]


/* ============================================================
   HOURS
============================================================ */

const hours = Array.from(
    { length: 24 },
    (_, index) => index
)


/* ============================================================
   EVENTS
============================================================ */
/*
    THIS IS WHERE YOUR DATABASE/API DATA WILL EVENTUALLY GO.

    You don't need to manually create calendar dates.

    Only provide the events.

    Later you can replace this with:
    
    const events = ref(await axios.get(...))
*/

// Sample events: placed in the CURRENT month, so they show when the calendar opens
const now = new Date()
const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
const sampleDate = (day, time = '') => `${thisMonth}-${String(day).padStart(2, '0')}${time ? ' ' + time : ''}`

const events = ref([

    { id: 1, title: 'All Day Event', date: sampleDate(1), allDay: true, description: 'long description', color: 'green', completed: true },

    { id: 2, title: 'Meeting', date: sampleDate(1), start: sampleDate(1, '10:30'), end: sampleDate(1, '11:30'), color: 'dark' },

    { id: 3, title: 'Lunch', date: sampleDate(1), start: sampleDate(1, '12:00'), end: sampleDate(1, '14:00'), color: 'dark' },

    { id: 4, title: 'Birthday Party', date: sampleDate(2), start: sampleDate(2, '19:00'), end: sampleDate(2, '21:00'), color: 'dark' },

    { id: 5, title: 'Repeating Event', date: sampleDate(5), start: sampleDate(5, '16:00'), end: sampleDate(5, '17:00'), color: 'blue', repeating: true },

    { id: 6, title: 'Repeating Event', date: sampleDate(12), start: sampleDate(12, '16:00'), end: sampleDate(12, '17:00'), color: 'blue', repeating: true },

    { id: 7, title: 'Long Event', date: sampleDate(20), start: sampleDate(20, '00:00'), end: sampleDate(20, '23:59'), color: 'red', locked: true },

    { id: 8, title: 'Smartadmin Open Day', date: sampleDate(28), start: sampleDate(28, '00:00'), end: sampleDate(28, '23:59'), color: 'dark' }

])


/* ============================================================
   VIEW LABEL
============================================================ */

const viewLabel = computed(() => {

    if (currentView.value === 'month')
        return 'Month'

    if (currentView.value === 'agenda')
        return 'Agenda'

    return 'Today'

})


/* ============================================================
   MONTH NAME
============================================================ */

const monthYear = computed(() => {

    return selectedDate.value.toLocaleDateString(
        'en-US',
        {
            month: 'long',
            year: 'numeric'
        }
    )

})


/* ============================================================
   TODAY TITLE
============================================================ */

const todayTitle = computed(() => {

    return selectedDate.value.toLocaleDateString(
        'en-US',
        {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        }
    )

})


/* ============================================================
   AGENDA TITLE
============================================================ */

const agendaTitle = computed(() => {

    const start = getStartOfWeek(selectedDate.value)

    const end = new Date(start)

    end.setDate(start.getDate() + 6)

    const startText = start.toLocaleDateString(
        'en-US',
        {
            month: 'short',
            day: 'numeric'
        }
    )

    const endText = end.toLocaleDateString(
        'en-US',
        {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        }
    )

    return `${startText} – ${endText}`

})


/* ============================================================
   MONTH DAYS
============================================================ */

const monthDays = computed(() => {

    const year = selectedDate.value.getFullYear()

    const month = selectedDate.value.getMonth()

    const firstDay = new Date(year, month, 1)

    const startDay = firstDay.getDay()

    const daysInMonth = new Date(
        year,
        month + 1,
        0
    ).getDate()

    const totalCells = 42

    const days = []

    for (let i = 0; i < totalCells; i++) {

        const date = new Date(
            year,
            month,
            i - startDay + 1
        )

        days.push({

            date,

            currentMonth:
                date.getMonth() === month,

            key:
                formatDateKey(date)

        })

    }

    return days

})


/* ============================================================
   AGENDA DAYS
============================================================ */

const agendaDays = computed(() => {

    const start = getStartOfWeek(
        selectedDate.value
    )

    return Array.from(
        { length: 7 },
        (_, index) => {

            const date = new Date(start)

            date.setDate(
                start.getDate() + index
            )

            return {

                date,

                key:
                    formatDateKey(date)

            }

        }
    )

})


/* ============================================================
   CHANGE VIEW
============================================================ */

function changeView(view) {

    currentView.value = view

}


/* ============================================================
   PREVIOUS
============================================================ */

function previousPeriod() {

    const date = new Date(
        selectedDate.value
    )

    if (currentView.value === 'month') {

        date.setMonth(
            date.getMonth() - 1
        )

    }

    else if (currentView.value === 'agenda') {

        date.setDate(
            date.getDate() - 7
        )

    }

    else {

        date.setDate(
            date.getDate() - 1
        )

    }

    selectedDate.value = date

}


/* ============================================================
   NEXT
============================================================ */

function nextPeriod() {

    const date = new Date(
        selectedDate.value
    )

    if (currentView.value === 'month') {

        date.setMonth(
            date.getMonth() + 1
        )

    }

    else if (currentView.value === 'agenda') {

        date.setDate(
            date.getDate() + 7
        )

    }

    else {

        date.setDate(
            date.getDate() + 1
        )

    }

    selectedDate.value = date

}


/* ============================================================
   GET START OF WEEK
============================================================ */

function getStartOfWeek(date) {

    const result = new Date(date)

    result.setHours(0, 0, 0, 0)

    result.setDate(
        result.getDate() - result.getDay()
    )

    return result

}


/* ============================================================
   FORMAT DATE KEY
============================================================ */

function formatDateKey(date) {

    const year =
        date.getFullYear()

    const month =
        String(
            date.getMonth() + 1
        ).padStart(2, '0')

    const day =
        String(
            date.getDate()
        ).padStart(2, '0')

    return `${year}-${month}-${day}`

}


/* ============================================================
   CHECK TODAY
============================================================ */

function isToday(date) {

    const today = new Date()

    return (
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
    )

}


/* ============================================================
   EVENTS FOR DATE
============================================================ */

function getEventsForDate(date) {

    const key =
        formatDateKey(date)

    return events.value.filter(
        event => event.date === key
    )

}


/* ============================================================
   ALL DAY EVENTS
============================================================ */

function getAllDayEvents(date) {

    return getEventsForDate(date)
        .filter(event => event.allDay)

}


/* ============================================================
   TIMED EVENTS
============================================================ */

function getTimedEvents(date) {

    return getEventsForDate(date)
        .filter(event => !event.allDay && event.start)

}


/* ============================================================
   DAY NAME
============================================================ */

function formatDayName(date) {

    return date.toLocaleDateString(
        'en-US',
        {
            weekday: 'short'
        }
    )

}


/* ============================================================
   SHORT DATE
============================================================ */

function formatShortDate(date) {

    return date.toLocaleDateString(
        'en-US',
        {
            month: 'numeric',
            day: 'numeric'
        }
    )

}


/* ============================================================
   HOUR FORMAT
============================================================ */

function formatHour(hour) {

    if (hour === 0)
        return '12am'

    if (hour < 12)
        return `${hour}am`

    if (hour === 12)
        return '12pm'

    return `${hour - 12}pm`

}


/* ============================================================
   EVENT TIME
============================================================ */

function formatEventTime(event) {

    if (!event.start)
        return '12a'

    const date =
        new Date(
            event.start.replace(' ', 'T')
        )

    // Short style like the picture: 7p, 10:30a, 12a
    const hour = date.getHours()
    const minutes = date.getMinutes()
    return `${hour % 12 || 12}${minutes ? ':' + String(minutes).padStart(2, '0') : ''}${hour < 12 ? 'a' : 'p'}`

}


/* ============================================================
   AGENDA EVENT TIME
============================================================ */

function formatAgendaEventTime(event) {

    if (!event.start)
        return ''

    const start =
        new Date(
            event.start.replace(' ', 'T')
        )

    const end =
        event.end
            ? new Date(
                event.end.replace(' ', 'T')
            )
            : null

    const startText =
        start.toLocaleTimeString(
            'en-US',
            {
                hour: 'numeric',
                minute: '2-digit'
            }
        )

    if (!end)
        return startText

    const endText =
        end.toLocaleTimeString(
            'en-US',
            {
                hour: 'numeric',
                minute: '2-digit'
            }
        )

    return `${startText} - ${endText}`

}


/* ============================================================
   EVENT POSITION
============================================================ */

function eventPosition(event) {

    if (!event.start)
        return {}

    const start =
        new Date(
            event.start.replace(' ', 'T')
        )

    const end =
        event.end
            ? new Date(
                event.end.replace(' ', 'T')
            )
            : new Date(
                start.getTime() + 60 * 60 * 1000
            )

    const startMinutes =
        start.getHours() * 60 +
        start.getMinutes()

    const endMinutes =
        end.getHours() * 60 +
        end.getMinutes()

    const top =
        startMinutes

    const height =
        Math.max(
            endMinutes - startMinutes,
            30
        )

    return {

        top: `${top}px`,

        height: `${height}px`

    }

}


/* ============================================================
   EVENT COLOR
============================================================ */

function eventClass(event) {

    return {

        'event-green':
            event.color === 'green',

        'event-red':
            event.color === 'red',

        'event-blue':
            event.color === 'blue',

        'event-dark':
            event.color === 'dark'

    }

}


/* ============================================================
   DRAG & DROP (move events to another date / time)
============================================================ */

const dragEventId = ref(null)   // the event being dragged
const dragOverKey = ref(null)   // the day under the mouse (month view highlight)
let grabOffsetY = 0             // where on the event it was grabbed (agenda view)

function startDrag(event, e) {
    if (event.locked) return
    dragEventId.value = event.id
    grabOffsetY = e.offsetY || 0
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(event.id))   // needed by Firefox
}

function endDrag() {
    dragEventId.value = null
    dragOverKey.value = null
}

function formatDateTime(date) {
    return `${formatDateKey(date)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

// Move an event to a new date; keeps its length. newStartMinutes = new time (agenda view)
function moveEvent(event, newDateKey, newStartMinutes = null) {
    event.date = newDateKey

    if (event.start || newStartMinutes !== null) {
        const oldStart = event.start ? new Date(event.start.replace(' ', 'T')) : null
        const oldEnd = event.end ? new Date(event.end.replace(' ', 'T')) : null
        const duration = oldStart && oldEnd ? oldEnd - oldStart : 60 * 60 * 1000
        const startMinutes = newStartMinutes !== null
            ? newStartMinutes
            : oldStart.getHours() * 60 + oldStart.getMinutes()

        const [y, m, d] = newDateKey.split('-').map(Number)
        const start = new Date(y, m - 1, d, Math.floor(startMinutes / 60), startMinutes % 60)
        event.start = formatDateTime(start)
        event.end = formatDateTime(new Date(start.getTime() + duration))
        if (newStartMinutes !== null) event.allDay = false   // dropped on a time: no longer all-day
    }

    emit('event-moved', event)
}

// Month view / all-day row: new date, same time
function dropOnDay(date) {
    const event = events.value.find(ev => ev.id === dragEventId.value)
    endDrag()
    if (!event || event.locked) return
    moveEvent(event, formatDateKey(date))
}

// Agenda view: new day AND new time (1px = 1 minute, snaps to 15 minutes)
function dropOnAgendaColumn(date, e) {
    const event = events.value.find(ev => ev.id === dragEventId.value)
    endDrag()
    if (!event || event.locked) return
    const rect = e.currentTarget.getBoundingClientRect()
    let minutes = Math.round((e.clientY - rect.top - grabOffsetY) / 15) * 15
    minutes = Math.min(Math.max(minutes, 0), 23 * 60 + 45)
    moveEvent(event, formatDateKey(date), minutes)
}

/* ============================================================
   EVENT CLICK
============================================================ */

function eventClicked(event) {

    console.log(
        'Selected event:',
        event
    )

}

</script>
<style scoped>
/* Day under the mouse while dragging an event */
.day.drag-over {
    outline: 2px dashed var(--sa-accent, #3276b1);
    outline-offset: -2px;
}
/* The event being dragged */
.event-dragging {
    opacity: 0.4;
}
.event[draggable="true"],
.agenda-event[draggable="true"] {
    cursor: grab;
}
</style>